/** How an external signal reads on screen. Pure functions, tested. */
import type { Signal } from './api';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** A rate or a score moves by points, not by a percentage of itself: inflation
 * going from 14.9% to 14.5% is -0.4 points, not "-2.7%". */
export function changesInPoints(unit: string | null | undefined): boolean {
  return (
    !!unit &&
    (unit === '%' || unit === 'pct' || unit === 'std dev' || isShare(unit))
  );
}

export function formatPoints(
  now: number | null | undefined,
  then: number | null | undefined,
  unit: string | null | undefined
): string {
  if (now === null || now === undefined || then === null || then === undefined)
    return '—';
  const d = isShare(unit) ? (now - then) * 100 : now - then;
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  return `${sign}${Math.abs(d).toFixed(Math.abs(d) >= 10 ? 1 : 2)} pts`;
}

/** A unit stored as a 0-1 share is shown as a percentage. */
export function isShare(unit: string | null | undefined): boolean {
  return !!unit && unit.startsWith('share');
}

export function formatValue(
  value: number | null | undefined,
  unit: string | null | undefined
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (isShare(unit)) return `${(value * 100).toFixed(1)}%`;
  if (unit === '%' || unit === 'pct') return `${value.toFixed(2)}%`;
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 10 ? 2 : 3;
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  });
}

/** The unit as a short suffix, or nothing when the value already carries it. */
export function unitLabel(unit: string | null | undefined): string {
  if (!unit || isShare(unit) || unit === '%' || unit === 'pct') return '';
  return unit;
}

/** "Sep 2026", "Q2 2026", "2025" or "5 Oct 2026", by the series' rhythm. */
export function periodLabel(
  iso: string | null | undefined,
  frequency: string
): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (frequency === 'annual') return String(y);
  if (frequency === 'quarterly') return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  if (frequency === 'monthly') return `${MONTHS[m - 1]} ${y}`;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
}

/** "vs previous month" — the comparison that change_pct was computed against. */
export function previousLabel(frequency: string): string {
  switch (frequency) {
    case 'daily':
      return 'vs previous day';
    case 'weekly':
      return 'vs previous week';
    case 'quarterly':
      return 'vs previous quarter';
    case 'annual':
      return 'vs previous year';
    default:
      return 'vs previous month';
  }
}

export function frequencyLabel(frequency: string): string {
  return (
    (
      {
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        annual: 'Annual'
      } as Record<string, string>
    )[frequency] ?? frequency
  );
}

export function formatPct(p: number | null | undefined): string {
  if (p === null || p === undefined) return '—';
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(Math.abs(p) >= 10 ? 0 : 1)}%`;
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Text a filter can match: name, source, family, unit. */
export function searchText(s: Signal): string {
  return `${s.name} ${s.source} ${s.family} ${s.unit ?? ''}`.toLowerCase();
}
